import React, { useState, useEffect } from 'react';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import './Settings.css';
import apiClient from '../../apiClient';

function RecruitmentPipeline({ onBack }) {
    const [activeTab, setActiveTab] = useState('status');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [newItemName, setNewItemName] = useState('');

    // Rejection Reasons State
    const [rejectionReasons, setRejectionReasons] = useState([]);
    const [rejectionLoading, setRejectionLoading] = useState(false);
    const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
    const [currentReason, setCurrentReason] = useState(null);
    const [reasonForm, setReasonForm] = useState({ reason_type: 'rejected', reason_name: '' });

    // Delete Modal State
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        itemId: null,
        itemName: null,
        deleteType: null // 'status', 'stage', or 'reason'
    });

    useEffect(() => {
        if (activeTab === 'status' || activeTab === 'stages') {
            fetchData();
        } else if (activeTab === 'reasons') {
            fetchRejectionReasons();
        }
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const endpoint = activeTab === 'status' ? '/ta/jobs/statuses' : '/ta/jobs/stages';
            const response = await apiClient.get(`${endpoint}`);
            let fetchedData = response.data.data;

            // Filter out Rejected (id=15) and Withdrawn (id=16) from stages
            if (activeTab === 'stages') {
                fetchedData = fetchedData.filter(item =>
                    !['Rejected', 'Withdrawn'].includes(item.stage_name) &&
                    ![15, 16].includes(item.id)
                );
            }

            setData(fetchedData);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const fetchRejectionReasons = async () => {
        setRejectionLoading(true);
        try {
            const response = await apiClient.get('/ta/jobs/rejection-reasons');
            setRejectionReasons(response.data.data || []);
        } catch (err) {
            console.error("Error fetching rejection reasons:", err);
        } finally {
            setRejectionLoading(false);
        }
    };

    const handleAdd = () => {
        setCurrentItem(null);
        setNewItemName('');
        setIsModalOpen(true);
    };

    const handleEdit = (item) => {
        setCurrentItem(item);
        setNewItemName(activeTab === 'status' ? item.ta_job_status : item.stage_name);
        setIsModalOpen(true);
    };

    const handleDelete = (item, deleteType = activeTab) => {
        setDeleteModal({
            isOpen: true,
            itemId: item.id,
            itemName: deleteType === 'reason' ? item.reason_name : (activeTab === 'status' ? item.ta_job_status : item.stage_name),
            deleteType: deleteType === 'reason' ? 'reason' : activeTab
        });
    };

    const handleConfirmDelete = async () => {
        try {
            let endpoint;
            if (deleteModal.deleteType === 'reason') {
                endpoint = `/ta/jobs/rejection-reasons/${deleteModal.itemId}`;
            } else {
                endpoint = deleteModal.deleteType === 'status'
                    ? `/ta/jobs/statuses/${deleteModal.itemId}`
                    : `/ta/jobs/stages/${deleteModal.itemId}`;
            }
            await apiClient.delete(endpoint);

            if (deleteModal.deleteType === 'reason') {
                fetchRejectionReasons();
            } else {
                fetchData();
            }
        } catch (err) {
            console.error("Error deleting item:", err);
            alert("Failed to delete item");
        } finally {
            setDeleteModal({ isOpen: false, itemId: null, itemName: null, deleteType: null });
        }
    };

    const handleSave = async () => {
        if (!newItemName.trim()) {
            alert("Name is required");
            return;
        }

        try {
            const endpoint = activeTab === 'status' ? '/ta/jobs/statuses' : '/ta/jobs/stages';
            const payload = activeTab === 'status' ? { ta_job_status: newItemName } : { stage_name: newItemName };

            if (currentItem) {
                await apiClient.put(`${endpoint}/${currentItem.id}`, payload);
            } else {
                await apiClient.post(`${endpoint}`, payload);
            }

            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            console.error("Error saving item:", err);
            alert(err.response?.data?.error || "Failed to save item");
        }
    };

    // Rejection Reason Handlers
    const handleAddReason = (type) => {
        setCurrentReason(null);
        setReasonForm({ reason_type: type, reason_name: '' });
        setIsReasonModalOpen(true);
    };

    const handleEditReason = (reason) => {
        setCurrentReason(reason);
        setReasonForm({ reason_type: reason.reason_type, reason_name: reason.reason_name });
        setIsReasonModalOpen(true);
    };

    const handleSaveReason = async () => {
        if (!reasonForm.reason_name.trim()) {
            alert("Reason name is required");
            return;
        }

        try {
            if (currentReason) {
                await apiClient.put(`/ta/jobs/rejection-reasons/${currentReason.id}`, reasonForm);
            } else {
                await apiClient.post('/ta/jobs/rejection-reasons', reasonForm);
            }
            setIsReasonModalOpen(false);
            fetchRejectionReasons();
        } catch (err) {
            console.error("Error saving reason:", err);
            alert(err.response?.data?.error || "Failed to save reason");
        }
    };

    const handleToggleReasonActive = async (reason) => {
        try {
            await apiClient.patch(`/ta/jobs/rejection-reasons/${reason.id}/toggle`);
            fetchRejectionReasons();
        } catch (err) {
            console.error("Error toggling reason:", err);
            alert("Failed to toggle reason status");
        }
    };

    // Group rejection reasons by type
    const rejectedReasons = rejectionReasons.filter(r => r.reason_type === 'rejected');
    const withdrawnReasons = rejectionReasons.filter(r => r.reason_type === 'withdrawn');

    return (
        <div className="settings-component-wrapper">
            <div className="settings-component-content">
                <div className="settings-component-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1>🔄 Recruitment Pipeline</h1>
                        <p>Configure recruitment stages and workflows</p>
                    </div>
                    <button className="settings-primary-button" onClick={onBack}>
                        ← Back to TA Settings
                    </button>
                </div>

                <div className="settings-sub-tabs">
                    <button
                        className={`settings-sub-tab ${activeTab === 'status' ? 'active' : ''}`}
                        onClick={() => setActiveTab('status')}
                    >
                        Job Statuses
                    </button>
                    <button
                        className={`settings-sub-tab ${activeTab === 'stages' ? 'active' : ''}`}
                        onClick={() => setActiveTab('stages')}
                    >
                        Candidate Stages
                    </button>
                    <button
                        className={`settings-sub-tab ${activeTab === 'reasons' ? 'active' : ''}`}
                        onClick={() => setActiveTab('reasons')}
                    >
                        Rejection & Withdrawal Reasons
                    </button>
                </div>

                {/* Job Statuses & Candidate Stages Tab Content */}
                {(activeTab === 'status' || activeTab === 'stages') && (
                    <div className="settings-component-body">
                        <div className="settings-action-bar">
                            <button className="settings-primary-button" onClick={handleAdd}>
                                + Add {activeTab === 'status' ? 'Status' : 'Stage'}
                            </button>
                        </div>

                        {loading ? (
                            <div className="settings-loading-state">Loading...</div>
                        ) : error ? (
                            <div className="settings-error-state">{error}</div>
                        ) : (
                            <div className="settings-table-container">
                                <table className="settings-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Name</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((item) => (
                                            <tr key={item.id}>
                                                <td>{item.id}</td>
                                                <td>{activeTab === 'status' ? item.ta_job_status : item.stage_name}</td>
                                                <td>
                                                    <div className="settings-action-buttons">
                                                        <button className="settings-edit-button" onClick={() => handleEdit(item)}>✏️</button>
                                                        <button className="settings-delete-button" onClick={() => handleDelete(item)}>🗑️</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {data.length === 0 && (
                                            <tr>
                                                <td colSpan="3" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No data found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Rejection & Withdrawal Reasons Tab Content */}
                {activeTab === 'reasons' && (
                    <div className="settings-component-body">
                        {rejectionLoading ? (
                            <div className="settings-loading-state">Loading...</div>
                        ) : (
                            <>
                                {/* Rejection Reasons Section */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h3 style={{ margin: 0, color: '#dc2626', fontSize: '1.1rem' }}>❌ Rejection Reasons</h3>
                                        <button className="settings-primary-button" onClick={() => handleAddReason('rejected')}>
                                            + Add Rejection Reason
                                        </button>
                                    </div>
                                    <div className="settings-table-container">
                                        <table className="settings-table">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Reason</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rejectedReasons.map((reason) => (
                                                    <tr key={reason.id}>
                                                        <td>{reason.id}</td>
                                                        <td>{reason.reason_name}</td>
                                                        <td>
                                                            <span className={`settings-status-badge ${reason.is_active ? 'active' : 'inactive'}`}>
                                                                {reason.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="settings-action-buttons">
                                                                <button
                                                                    className="settings-edit-button"
                                                                    onClick={() => handleToggleReasonActive(reason)}
                                                                    title={reason.is_active ? 'Deactivate' : 'Activate'}
                                                                >
                                                                    {reason.is_active ? '🔴' : '🟢'}
                                                                </button>
                                                                <button className="settings-edit-button" onClick={() => handleEditReason(reason)}>✏️</button>
                                                                <button className="settings-delete-button" onClick={() => handleDelete(reason, 'reason')}>🗑️</button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {rejectedReasons.length === 0 && (
                                                    <tr>
                                                        <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                                                            No rejection reasons configured
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Withdrawal Reasons Section */}
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h3 style={{ margin: 0, color: '#f59e0b', fontSize: '1.1rem' }}>🚪 Withdrawal Reasons</h3>
                                        <button className="settings-primary-button" onClick={() => handleAddReason('withdrawn')}>
                                            + Add Withdrawal Reason
                                        </button>
                                    </div>
                                    <div className="settings-table-container">
                                        <table className="settings-table">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Reason</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {withdrawnReasons.map((reason) => (
                                                    <tr key={reason.id}>
                                                        <td>{reason.id}</td>
                                                        <td>{reason.reason_name}</td>
                                                        <td>
                                                            <span className={`settings-status-badge ${reason.is_active ? 'active' : 'inactive'}`}>
                                                                {reason.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="settings-action-buttons">
                                                                <button
                                                                    className="settings-edit-button"
                                                                    onClick={() => handleToggleReasonActive(reason)}
                                                                    title={reason.is_active ? 'Deactivate' : 'Activate'}
                                                                >
                                                                    {reason.is_active ? '🔴' : '🟢'}
                                                                </button>
                                                                <button className="settings-edit-button" onClick={() => handleEditReason(reason)}>✏️</button>
                                                                <button className="settings-delete-button" onClick={() => handleDelete(reason, 'reason')}>🗑️</button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {withdrawnReasons.length === 0 && (
                                                    <tr>
                                                        <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                                                            No withdrawal reasons configured
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Status/Stage Modal */}
            {isModalOpen && (
                <div className="settings-modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="settings-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="settings-modal-header">
                            <h2>{currentItem ? 'Edit' : 'Add'} {activeTab === 'status' ? 'Status' : 'Stage'}</h2>
                            <button className="settings-close-button" onClick={() => setIsModalOpen(false)}>×</button>
                        </div>
                        <div className="settings-form-group">
                            <label>Name:</label>
                            <input
                                type="text"
                                value={newItemName}
                                onChange={(e) => setNewItemName(e.target.value)}
                                placeholder={`Enter ${activeTab === 'status' ? 'status' : 'stage'} name`}
                            />
                        </div>
                        <div className="settings-modal-actions">
                            <button className="settings-cancel-button" onClick={() => setIsModalOpen(false)}>Cancel</button>
                            <button className="settings-submit-button" onClick={handleSave}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rejection/Withdrawal Reason Modal */}
            {isReasonModalOpen && (
                <div className="settings-modal-overlay" onClick={() => setIsReasonModalOpen(false)}>
                    <div className="settings-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="settings-modal-header">
                            <h2>
                                {currentReason ? 'Edit' : 'Add'} {reasonForm.reason_type === 'rejected' ? 'Rejection' : 'Withdrawal'} Reason
                            </h2>
                            <button className="settings-close-button" onClick={() => setIsReasonModalOpen(false)}>×</button>
                        </div>
                        <div className="settings-form-group">
                            <label>Type:</label>
                            <select
                                value={reasonForm.reason_type}
                                onChange={(e) => setReasonForm({ ...reasonForm, reason_type: e.target.value })}
                                disabled={currentReason !== null}
                            >
                                <option value="rejected">Rejection Reason</option>
                                <option value="withdrawn">Withdrawal Reason</option>
                            </select>
                        </div>
                        <div className="settings-form-group">
                            <label>Reason Name:</label>
                            <input
                                type="text"
                                value={reasonForm.reason_name}
                                onChange={(e) => setReasonForm({ ...reasonForm, reason_name: e.target.value })}
                                placeholder="Enter reason name"
                            />
                        </div>
                        <div className="settings-modal-actions">
                            <button className="settings-cancel-button" onClick={() => setIsReasonModalOpen(false)}>Cancel</button>
                            <button className="settings-submit-button" onClick={handleSaveReason}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, itemId: null, itemName: null, deleteType: null })}
                onConfirm={handleConfirmDelete}
                itemName={deleteModal.itemName}
            />
        </div>
    );
}

export default RecruitmentPipeline;
